import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import {
  POSTHOG_FEATURE_FLAGS,
  PostHogFeatureFlag,
  useActivePostHogFeatureFlags,
  usePostHogFeatureFlag
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag'
import { usePermissions } from '@percent/admin-dashboard/common/hooks'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { Navigation } from '@percent/lemonade'
import { createStorageAdapter } from '@percent/utility'
import { AdminLogo } from './AdminLogo'
import { AccountSearch } from './AccountSearch'
import { getADNavItems } from './getADNavItems'
import { BadgeCounts } from './getADNavItems.types'

const STORAGE_KEY = 'ad-navbar-preferences'

type SideNavigationProps = {
  isCollapsed?: boolean
  onToggleCollapsed?: VoidFunction
}

export function SideNavigation({ isCollapsed, onToggleCollapsed }: SideNavigationProps) {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const activeFeatureFlags = useActivePostHogFeatureFlags().filter((flag): flag is PostHogFeatureFlag =>
    (Object.values(POSTHOG_FEATURE_FLAGS) as string[]).includes(flag)
  )
  const bankDetailsReadFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.AD_BANK_DETAILS_READ)
  const { userHasPermissions } = usePermissions()
  const { bankDetailsService } = useServices()
  const [badgeCounts, setBadgeCounts] = useState<BadgeCounts>({})

  const storage = useMemo(() => createStorageAdapter('localStorage'), [])

  useEffect(() => {
    if (bankDetailsReadFeatureFlag) {
      bankDetailsService
        .getBankDetailsList({ statusFilter: ['pending'] })
        .then(response => {
          setBadgeCounts(prev => ({ ...prev, bankDetails: response.data.totalResults ?? 0 }))
        })
        .catch(() => {
          setBadgeCounts(prev => ({ ...prev, bankDetails: 0 }))
        })
    }
  }, [bankDetailsReadFeatureFlag, bankDetailsService])

  const navItems = getADNavItems({
    pathname,
    activeFeatureFlags,
    t,
    badgeCounts,
    userHasPermissions
  })

  return (
    <Navigation
      logo={<AdminLogo />}
      collapsedLogo={<AdminLogo isMark />}
      items={navItems}
      storageKey={STORAGE_KEY}
      storage={storage}
      headerContent={<AccountSearch />}
      collapsedHeaderContent={<AccountSearch isCompact />}
      isCollapsed={isCollapsed}
      onToggleCollapsed={onToggleCollapsed}
      collapseLabel={t('menu.collapseNavigation')}
      expandLabel={t('menu.expandNavigation')}
    />
  )
}
